import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/currencyUtils';
import { getAnnualCTC, getAnnualBasic, getAnnualBonus } from '../../utils/salaryUtils';
import SalaryPaymentHistory from './SalaryPaymentHistory';
import { SALARY_THEME } from './salaryTheme';
import { filterActiveEmployees } from '../../utils/employeeStatusUtils';
import { Banknote, ChevronDown, Plus, Search, TrendingDown, TrendingUp, X } from 'lucide-react';
import AppSelect from '../shared/AppSelect';

const field =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30';
const fieldSelect =
  `${field} text-left min-h-[44px] flex items-center justify-between`;
const label = 'mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide';

function LineRow({ children, onRemove, locked }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-2.5 border-b border-gray-100 last:border-0 hover:bg-blue-50/40 transition-colors px-1">
      {children}
      {locked ? (
        <span className="text-[10px] uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
          Profile
        </span>
      ) : onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-gray-400 hover:text-red-500 rounded"
          aria-label="Remove"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

export default function SalaryProcessingTab(props) {
  const {
    t,
    companyCurrency,
    salaryPayments,
    irregularPaymentsForEmployee,
    selectedEmp,
    totalEarnings,
    totalDeductions,
    netSalary,
    totalPaid,
    employeesNeedingMigration,
    salaryError,
    salaryEmployeeId,
    salaryDate,
    salaryMonth,
    salaryPaymentMode,
    fixedSalaryRows,
    performanceSalaryRows,
    deductionRows,
    adjustSalaryFromBalance,
    salaryBalanceSource,
    salaryBalanceAmount,
    adjustSalaryFromOtherWallets,
    salarySourceEmployeeId,
    salarySourceEmployeeBalance,
    salaryOtherWalletsAmount,
    salaryBalanceSourceOther,
    appliedIrregularSelections,
    showEmployeeDropdown,
    salarySearch,
    selectedEmployeeDisplay,
    selectedEmployeeIndex,
    filteredEmployeeOptions,
    expandedSalaryCards,
    contextEmployees,
    setSalaryEmployeeId,
    setSalaryDate,
    setSalaryMonth,
    setSalaryPaymentMode,
    setFixedSalaryRows,
    setPerformanceSalaryRows,
    setSalaryDeductions,
    setAdjustSalaryFromBalance,
    setSalaryBalanceSource,
    setSalaryBalanceAmount,
    setAdjustSalaryFromOtherWallets,
    setSalarySourceEmployeeId,
    resetOtherWalletSettlement,
    setSalaryOtherWalletsAmount,
    setSalaryBalanceSourceOther,
    setSalarySearch,
    setSelectedEmployeeDisplay,
    setSelectedEmployeeIndex,
    setExpandedSalaryCards,
    handleDropdownToggle,
    handleEmployeeKeyDown,
    handleEmployeeSelect,
    handleFixedSalaryRowChange,
    addFixedSalaryRow,
    removeFixedSalaryRow,
    handlePerformanceRowChange,
    addPerformanceRow,
    removePerformanceRow,
    handleDeductionRowChange,
    addDeductionRow,
    removeDeductionRow,
    handleIrregularToggle,
    handleIrregularAppliedAsChange,
    handleSalarySubmit,
    handleViewSalarySlip,
    handleEditSalaryPayment,
    handleDeleteSalaryPayment,
    handleExportSalaryCSV,
    handleExportSalaryExcel,
    handleExportSalaryPDF,
    info,
  } = props;

  const [view, setView] = useState('payroll');
  const fmt = (n) => formatCurrency(Number(n) || 0, companyCurrency);
  const salaryUi = SALARY_THEME;

  const clearForm = () => {
    setSalaryEmployeeId('');
    setSelectedEmployeeDisplay('');
    setFixedSalaryRows([]);
    setPerformanceSalaryRows([]);
    setSalaryDeductions([]);
    resetOtherWalletSettlement();
    setAdjustSalaryFromBalance(false);
    setAdjustSalaryFromOtherWallets(false);
    setSalaryBalanceAmount(0);
  };

  const otherWalletEmployees = filterActiveEmployees(contextEmployees).filter(
    (e) => e.id !== selectedEmp?.id && e.employeeId !== salaryEmployeeId
  );

  return (
    <div className="space-y-4 -mt-2">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden ring-1 ring-black/5">
        <div className={`bg-gradient-to-r ${salaryUi.header} px-5 py-5 text-white`}>
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500/25 ring-1 ring-white/20 backdrop-blur-sm">
              <Banknote className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Salary processing</h2>
              <p className="text-slate-300 text-sm mt-0.5">
                Run payroll, view payment history, and export salary records
              </p>
            </div>
          </div>
        </div>
      </div>

      {employeesNeedingMigration.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="flex-1">
            {employeesNeedingMigration.length} employee(s) still on the old salary format.{' '}
            <Link to="/employee-management" className="font-medium text-blue-600 underline">
              Update in Employee Management
            </Link>
          </p>
        </div>
      )}

      {/* Sub-navigation */}
      <div className={salaryUi.subNavWrap}>
        <button
          type="button"
          onClick={() => setView('payroll')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            view === 'payroll' ? salaryUi.subNavActive : salaryUi.subNavIdle
          }`}
        >
          <Banknote className="h-4 w-4" />
          Run payroll
        </button>
        <button
          type="button"
          onClick={() => setView('history')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            view === 'history' ? salaryUi.subNavActive : salaryUi.subNavIdle
          }`}
        >
          History
          {salaryPayments.length > 0 && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                view === 'history' ? salaryUi.subNavBadgeActive : salaryUi.subNavBadgeIdle
              }`}
            >
              {salaryPayments.length}
            </span>
          )}
        </button>
      </div>

      {view === 'history' ? (
        <SalaryPaymentHistory
          salaryPayments={salaryPayments}
          totalPaid={totalPaid}
          companyCurrency={companyCurrency}
          expandedSalaryCards={expandedSalaryCards}
          setExpandedSalaryCards={setExpandedSalaryCards}
          onViewSlip={handleViewSalarySlip}
          onEdit={handleEditSalaryPayment}
          onDelete={handleDeleteSalaryPayment}
          onExportCSV={handleExportSalaryCSV}
          onExportExcel={handleExportSalaryExcel}
          onExportPDF={handleExportSalaryPDF}
        />
      ) : (
        <form
          onSubmit={handleSalarySubmit}
          className="bg-white rounded-xl border border-gray-200 shadow-lg ring-1 ring-black/5 overflow-hidden"
        >
          <div className={`h-1 bg-gradient-to-r ${salaryUi.topStripe}`} aria-hidden />
          {salaryError && (
            <div className="mx-5 mt-5 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700" role="alert">
              {salaryError}
            </div>
          )}

          {/* Who & when */}
          <div className="p-5 border-b border-gray-100 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
            <p className="flex items-center gap-2 text-xs font-bold text-blue-800 uppercase tracking-wider mb-4">
              <span className="h-5 w-1 rounded-full bg-blue-600" aria-hidden />
              Who & when
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="md:col-span-2 relative employee-dropdown">
                <label className={label}>Employee</label>
                <button
                  type="button"
                  onClick={handleDropdownToggle}
                  className={`${field} flex items-center justify-between text-left`}
                >
                  <span className={selectedEmployeeDisplay ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedEmployeeDisplay || 'Select employee…'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                </button>
                {showEmployeeDropdown && (
                  <div
                    className="absolute z-[100] mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg"
                    style={{ zIndex: 9999 }}
                  >
                    <div className="flex items-center gap-2 border-b border-gray-100 px-3">
                      <Search className="h-4 w-4 text-gray-400 shrink-0" />
                      <input
                        type="text"
                        value={salarySearch}
                        onChange={(e) => {
                          setSalarySearch(e.target.value);
                          setSelectedEmployeeIndex(-1);
                        }}
                        onKeyDown={handleEmployeeKeyDown}
                        placeholder="Search by name or ID"
                        className="flex-1 py-2.5 text-sm border-0 focus:ring-0"
                        autoFocus
                      />
                    </div>
                    <ul className="max-h-52 overflow-y-auto py-1">
                      {filteredEmployeeOptions.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-500">No matches</li>
                      ) : (
                        filteredEmployeeOptions.map((opt, idx) => (
                          <li key={opt.value}>
                            <button
                              type="button"
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 ${
                                idx === selectedEmployeeIndex ? 'bg-blue-100' : ''
                              }`}
                              onMouseDown={() => handleEmployeeSelect(opt)}
                            >
                              {opt.label}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <label className={label}>Salary month</label>
                <input
                  type="month"
                  value={salaryMonth}
                  onChange={(e) => setSalaryMonth(e.target.value)}
                  className={field}
                  required
                />
              </div>
              <div>
                <label className={label}>Payment date</label>
                <input
                  type="date"
                  value={salaryDate}
                  onChange={(e) => setSalaryDate(e.target.value)}
                  className={field}
                  required
                />
              </div>
              <div className="md:col-span-2 lg:col-span-1">
                <label className={label}>Payment mode</label>
                <AppSelect
                  value={salaryPaymentMode || 'Cash'}
                  onChange={(e) => setSalaryPaymentMode(e.target.value)}
                  aria-label="Payment mode"
                  sheetTitle="Payment mode"
                  options={[
                    { value: 'Cash', label: t('cash') },
                    { value: 'Bank Transfer', label: t('bankTransfer') },
                    { value: 'UPI', label: 'UPI' },
                    { value: 'Cheque', label: t('cheque') },
                    { value: 'Card', label: t('card') },
                    { value: 'Other', label: t('other') },
                  ]}
                  triggerClassName={fieldSelect}
                />
              </div>
            </div>

            {selectedEmp && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-bold text-blue-600 uppercase">Annual CTC</p>
                  <p className="text-sm font-bold text-gray-900">{fmt(getAnnualCTC(selectedEmp))}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Basic</p>
                  <p className="text-sm font-bold text-gray-900">{fmt(getAnnualBasic(selectedEmp))}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Bonus</p>
                  <p className="text-sm font-bold text-gray-900">{fmt(getAnnualBonus(selectedEmp))}</p>
                </div>
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-bold text-sky-700 uppercase">Monthly</p>
                  <p className="text-sm font-bold text-sky-900">{fmt(getAnnualCTC(selectedEmp) / 12)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Pay breakdown */}
          <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className={`rounded-xl border p-4 shadow-sm ${salaryUi.earningsCard}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`flex items-center gap-2 text-sm font-bold ${salaryUi.earningsTitle}`}>
                  <TrendingUp className="h-4 w-4" />
                  Earnings
                </h3>
                <button
                  type="button"
                  onClick={addFixedSalaryRow}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5"
                >
                  <Plus className="h-3.5 w-3.5" /> Line
                </button>
              </div>
              {!selectedEmp ? (
                <p className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                  Select an employee to load basic & bonus
                </p>
              ) : (
                <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 px-3">
                  {fixedSalaryRows.map((row, idx) => (
                    <LineRow
                      key={`f-${idx}`}
                      locked={!row.removable}
                      onRemove={row.removable ? () => removeFixedSalaryRow(idx) : null}
                    >
                      <input
                        type="text"
                        value={row.type || ''}
                        onChange={(e) => handleFixedSalaryRowChange(idx, 'type', e.target.value)}
                        className="flex-1 min-w-[100px] border-0 bg-transparent text-sm font-medium focus:ring-0 p-0"
                      />
                      <input
                        type="number"
                        value={row.amount}
                        onChange={(e) => handleFixedSalaryRowChange(idx, 'amount', e.target.value)}
                        className="w-28 border border-gray-200 rounded-md px-2 py-1 text-sm text-right"
                        min="0"
                        step="0.01"
                      />
                    </LineRow>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-gray-500 uppercase">Variable pay</h4>
                  <button
                    type="button"
                    onClick={addPerformanceRow}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    + Add
                  </button>
                </div>
                {performanceSalaryRows.length === 0 ? (
                  <p className="text-xs text-gray-400">Incentive, overtime, commission…</p>
                ) : (
                  <div className="space-y-2">
                    {performanceSalaryRows.map((row, idx) => (
                      <div key={idx} className="rounded-lg border border-gray-200 p-3 space-y-2">
                        <div className="flex gap-2">
                          <AppSelect
                            value={row.type}
                            onChange={(e) => handlePerformanceRowChange(idx, 'type', e.target.value)}
                            placeholder="Type"
                            aria-label="Performance type"
                            sheetTitle="Type"
                            options={[
                              { value: '', label: 'Type' },
                              { value: 'Advance', label: t('advance') },
                              { value: 'Incentive', label: 'Incentive' },
                              { value: 'Bonus', label: 'Bonus' },
                              { value: 'Overtime', label: 'Overtime' },
                              { value: 'Commission', label: 'Commission' },
                              { value: 'Other', label: 'Other' },
                            ]}
                            triggerClassName="flex-1 text-sm border border-gray-200 rounded-md px-2 py-1.5 text-left min-h-[44px] flex items-center justify-between"
                          />
                          <input
                            type="number"
                            value={row.amount}
                            onChange={(e) => handlePerformanceRowChange(idx, 'amount', e.target.value)}
                            className="w-24 text-sm border border-gray-200 rounded-md px-2 py-1.5 text-right"
                            required
                            min="0"
                            step="0.01"
                          />
                          <button type="button" onClick={() => removePerformanceRow(idx)} className="text-gray-400 hover:text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        {row.type === 'Other' && (
                          <input
                            type="text"
                            value={row.otherType || ''}
                            onChange={(e) => handlePerformanceRowChange(idx, 'otherType', e.target.value)}
                            placeholder="Description"
                            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                            required
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-red-100 bg-gradient-to-br from-red-50/40 to-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="flex items-center gap-2 text-sm font-bold text-red-900">
                  <TrendingDown className="h-4 w-4" />
                  Deductions
                </h3>
                <button
                  type="button"
                  onClick={addDeductionRow}
                  className="text-xs font-medium text-red-700 hover:text-red-900 bg-red-50 px-2 py-1 rounded-md"
                >
                  + Add
                </button>
              </div>
              {deductionRows.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                  No deductions
                </p>
              ) : (
                <div className="space-y-2">
                  {deductionRows.map((row, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-3 space-y-2">
                      <div className="flex gap-2">
                        <AppSelect
                          value={row.reason}
                          onChange={(e) => handleDeductionRowChange(idx, 'reason', e.target.value)}
                          placeholder="Reason"
                          aria-label="Deduction reason"
                          sheetTitle="Reason"
                          options={[
                            { value: '', label: 'Reason' },
                            { value: 'Advance Repayment', label: 'Advance Repayment' },
                            { value: 'Unpaid Leave', label: 'Unpaid Leave' },
                            { value: 'Late Coming', label: 'Late Coming' },
                            { value: 'Loan Repayment', label: 'Loan Repayment' },
                            { value: 'Insurance', label: 'Insurance' },
                            { value: 'Tax Deduction', label: 'Tax Deduction' },
                            { value: 'Other', label: 'Other' },
                          ]}
                          triggerClassName="flex-1 text-sm border border-gray-200 rounded-md px-2 py-1.5 text-left min-h-[44px] flex items-center justify-between"
                        />
                        <input
                          type="number"
                          value={row.amount}
                          onChange={(e) => handleDeductionRowChange(idx, 'amount', e.target.value)}
                          className="w-24 text-sm border border-gray-200 rounded-md px-2 py-1.5 text-right"
                          required
                          min="0"
                          step="0.01"
                        />
                        <button type="button" onClick={() => removeDeductionRow(idx)} className="text-gray-400 hover:text-red-500">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {irregularPaymentsForEmployee.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Pending advances</h4>
                  <ul className="space-y-2">
                    {irregularPaymentsForEmployee.map((item, idx) => {
                      const on = appliedIrregularSelections[item.id] !== undefined;
                      const as = appliedIrregularSelections[item.id] || 'Earning';
                      return (
                        <li
                          key={item.id}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                            on ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => handleIrregularToggle(idx, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="flex-1 font-medium text-gray-800">
                            {item.paymentType} · {fmt(item.amount)}
                          </span>
                          <AppSelect
                            value={as}
                            disabled={!on}
                            onChange={(e) => handleIrregularAppliedAsChange(idx, e.target.value)}
                            aria-label="Apply as"
                            sheetTitle="Apply as"
                            options={[
                              { value: 'Earning', label: '+' },
                              { value: 'Deduction', label: '−' },
                            ]}
                            triggerClassName="text-xs border border-gray-200 rounded px-2 py-1 disabled:opacity-40 text-left min-h-[44px] flex items-center justify-between"
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Wallet — collapsed by default */}
          <details className="border-t border-gray-100 group">
            <summary className="px-5 py-3 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 list-none flex items-center justify-between">
              <span>Wallet settlement (optional)</span>
              <ChevronDown className="h-4 w-4 text-gray-400 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="px-5 pb-5 space-y-4 bg-gray-50/50">
              <label className="flex gap-3 items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={adjustSalaryFromBalance}
                  onChange={(e) => {
                    setAdjustSalaryFromBalance(e.target.checked);
                    setSalaryBalanceAmount(e.target.checked ? netSalary : 0);
                  }}
                  className="mt-1 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700">Pay from this employee&apos;s cash balance</span>
              </label>
              {adjustSalaryFromBalance && selectedEmp && (
                <div className="ml-7 grid sm:grid-cols-2 gap-3 max-w-md">
                  <AppSelect
                    value={salaryBalanceSource}
                    onChange={(e) => setSalaryBalanceSource(e.target.value)}
                    aria-label="Balance source"
                    sheetTitle="Balance source"
                    options={[
                      { value: 'both', label: 'Cash + UPI' },
                      { value: 'cash', label: 'Cash only' },
                      { value: 'upi', label: 'UPI only' },
                    ]}
                    triggerClassName={fieldSelect}
                  />
                  <input
                    type="number"
                    value={salaryBalanceAmount}
                    onChange={(e) => setSalaryBalanceAmount(parseFloat(e.target.value) || 0)}
                    max={netSalary}
                    className={field}
                    placeholder="Amount"
                  />
                </div>
              )}

              <label className="flex gap-3 items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={adjustSalaryFromOtherWallets}
                  onChange={(e) => {
                    setAdjustSalaryFromOtherWallets(e.target.checked);
                    if (!e.target.checked) resetOtherWalletSettlement();
                    else {
                      resetOtherWalletSettlement();
                      info('Choose whose wallet to deduct from.', { title: 'Wallet', duration: 4000 });
                    }
                  }}
                  className="mt-1 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700">Pay from another employee&apos;s wallet</span>
              </label>
              {adjustSalaryFromOtherWallets && selectedEmp && otherWalletEmployees.length > 0 && (
                <div className="ml-7 grid sm:grid-cols-2 gap-3 max-w-lg">
                  <AppSelect
                    value={salarySourceEmployeeId}
                    onChange={(e) => {
                      setSalarySourceEmployeeId(e.target.value);
                      setSalaryOtherWalletsAmount(0);
                    }}
                    placeholder="Source employee"
                    aria-label="Source employee"
                    sheetTitle="Source employee"
                    options={[
                      { value: '', label: 'Source employee' },
                      ...otherWalletEmployees.map((emp) => ({
                        value: emp.id,
                        label: emp.name,
                      })),
                    ]}
                    triggerClassName={fieldSelect}
                  />
                  {salarySourceEmployeeId && salarySourceEmployeeBalance && (
                    <>
                      <p className="sm:col-span-2 text-xs text-gray-500">
                        Available ₹{(salarySourceEmployeeBalance.total || 0).toLocaleString('en-IN')}
                      </p>
                      <AppSelect
                        value={salaryBalanceSourceOther}
                        onChange={(e) => setSalaryBalanceSourceOther(e.target.value)}
                        aria-label="Other wallet balance source"
                        sheetTitle="Balance source"
                        options={[
                          { value: 'both', label: 'Cash + UPI' },
                          { value: 'cash', label: 'Cash' },
                          { value: 'upi', label: 'UPI' },
                        ]}
                        triggerClassName={fieldSelect}
                      />
                      <input
                        type="number"
                        value={salaryOtherWalletsAmount}
                        onChange={(e) => setSalaryOtherWalletsAmount(parseFloat(e.target.value) || 0)}
                        max={netSalary}
                        className={field}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </details>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 border-t border-slate-700 bg-gradient-to-r from-slate-800 via-slate-800 to-slate-900 text-white rounded-b-xl">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wide">Earnings</p>
                <p className={`font-semibold ${salaryUi.earningsMuted}`}>{fmt(totalEarnings)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wide">Deductions</p>
                <p className="font-semibold text-red-400">{fmt(totalDeductions)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wide">Net pay</p>
                <p className="text-xl font-bold">{fmt(netSalary)}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={clearForm}
                className="px-4 py-2.5 text-sm font-medium text-slate-800 bg-white rounded-lg hover:bg-slate-100"
              >
                Clear
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 text-sm font-semibold text-slate-900 bg-gradient-to-r from-sky-300 to-blue-400 rounded-lg hover:brightness-110 shadow-lg"
              >
                Save payment
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
